/**
 * Hook React Query para el detail del Action Center (0666b).
 *
 * Compone 2 endpoints en paralelo (`useQueries`-like vía dos useQuery):
 *   1. `/action-center/detail/:tipo/:id` (creado en 0666a) → artefacto + notificados
 *   2. `/workflow/timeline/:tipo/:id` (existente desde 0656) → flujoAprobacion[]
 *
 * Solo se ejecuta si `enabled = true` (el panel solo se monta cuando el
 * usuario selecciona una fila).
 */

import { useQuery } from '@tanstack/react-query';
import { request } from '../../../shared/services/api';
import type { ActionItemTipo } from './useActionCenterList';

export type ArtefactoWorkflowTipo = 'ORGANIGRAMA' | 'DOC' | 'FLUJOGRAMA' | 'PROCESO';

export interface DetailArtefacto {
  tipo: ArtefactoWorkflowTipo;
  id: string;
  codigo: string | null;
  titulo: string;
  asunto: string | null;
  alcance: string | null;
  estado: string;
  creadoEn: string;
  creadoPor: string | null;
}

export interface DetailNotificado {
  id: string;
  destinatarioPersonaId: string | null;
  destinatarioNombre: string | null;
  destinatarioEmail: string | null;
  canal: string;
  status: string;
  enviadoEn: string;
  /** Rol de gobernanza del destinatario (enriquecido en 0669). */
  rolGobernanza: string | null;
  /** Puesto activo del destinatario (enriquecido en 0669). */
  puesto: { titulo: string; codigo: string | null } | null;
}

export interface ActionCenterDetailResponse {
  data: {
    artefacto: DetailArtefacto;
    notificados: DetailNotificado[];
    adjuntos: never[];
    comentariosTimeline: never[];
  };
}

/**
 * Shape canónica del backend (ver `workflow-timeline.service.ts`).
 * IMPORTANTE: el backend devuelve `actor` (singular) NO `actores`, y los
 * estados son DONE/PENDING/FUTURE/SKIPPED (no COMPLETADA/EN_CURSO/PENDIENTE/OMITIDA).
 * Mantener este tipo alineado con `TimelineEtapa` del backend.
 */
export interface WorkflowTimelineEtapa {
  orden: number;
  posicion: number;
  totalEtapas: number;
  accion: 'REVISAR' | 'APROBAR' | 'NOTIFICAR';
  modo: 'TODOS' | 'PRIMERO';
  rolEsperado: string | null;
  actor: {
    personaId: string | null;
    nombreCompleto: string | null;
    fecha: string | null;
    /** Puesto activo del actor (enriquecido en 0669). */
    puesto: { titulo: string; codigo: string | null } | null;
  };
  decision: 'APROBAR' | 'RECHAZAR' | null;
  motivo: string | null;
  estado: 'DONE' | 'PENDING' | 'FUTURE' | 'SKIPPED';
}

export interface WorkflowTimelineResponse {
  data: {
    artefactoTipo: string;
    artefactoId: string;
    estadoArtefacto: string | null;
    solicitante: {
      personaId: string | null;
      nombreCompleto: string | null;
      fecha: string | null;
    } | null;
    etapas: WorkflowTimelineEtapa[];
    publicacion: {
      personaId: string | null;
      nombreCompleto: string | null;
      fecha: string | null;
      estado: 'DONE' | 'PENDING' | 'FUTURE' | 'SKIPPED';
    };
  } | null;
}

/**
 * Mapea tipo del listado (puede ser legacy_sign) a tipo del motor unificado.
 * Devuelve null si el tipo no tiene correspondencia (legacy puro · no hay
 * detail nuevo, se mostrará la URL acción legacy y los datos de la fila).
 */
export function tipoAtipoArtefacto(tipo: ActionItemTipo): ArtefactoWorkflowTipo | null {
  if (tipo === 'ORGANIGRAMA' || tipo === 'DOC' || tipo === 'FLUJOGRAMA') return tipo;
  return null;
}

export function useActionCenterDetail(
  tipo: ArtefactoWorkflowTipo | null,
  id: string | null,
  enabled: boolean,
) {
  const detailQuery = useQuery({
    queryKey: ['actionCenter', 'detail', tipo, id] as const,
    queryFn: async () => {
      if (!tipo || !id) throw new Error('detail requires tipo+id');
      return request<ActionCenterDetailResponse>(`/action-center/detail/${tipo}/${id}`);
    },
    enabled: enabled && Boolean(tipo) && Boolean(id),
  });

  const timelineQuery = useQuery({
    queryKey: ['workflow', 'timeline', tipo, id] as const,
    queryFn: async () => {
      if (!tipo || !id) throw new Error('timeline requires tipo+id');
      return request<WorkflowTimelineResponse>(`/workflow/timeline/${tipo}/${id}`);
    },
    enabled: enabled && Boolean(tipo) && Boolean(id),
  });

  return {
    detail: detailQuery.data?.data ?? null,
    timeline: timelineQuery.data?.data ?? null,
    isLoading: detailQuery.isLoading || timelineQuery.isLoading,
    isError: detailQuery.isError || timelineQuery.isError,
    error: detailQuery.error ?? timelineQuery.error ?? null,
  };
}
